"use client"

import { useMemo } from "react"
import { useCmdkContext } from "../context"
import { CmdkItem } from "../types"
import {
  useDeepCompareEffect,
  useMemoizedFn,
  useUnmount,
} from "@seamapi/shared/react-hooks"

export interface CmdkCreateItemsOptions {
  groupName: string | null
  key: string
  items: (CmdkItem | null | boolean | undefined)[]
  weight?: number
  // Skip the palette's local fuzzy filtering for this group (items are already
  // scoped to the query by their source, e.g. a server-side search).
  serverFiltered?: boolean
  // Only list this group while the palette's `page` sub-page is open.
  page?: string
}

export function useCreateCmdkItems(options: CmdkCreateItemsOptions) {
  const { register, unregister } = useCmdkContext()
  const {
    key,
    items: _items,
    groupName,
    weight = 0,
    serverFiltered,
    page,
  } = options

  const filteredItems = useMemo(
    () => _items.filter(Boolean) as CmdkItem[],
    [_items],
  )

  const performAction = useMemoizedFn((item: CmdkItem) => {
    for (const i of filteredItems) {
      if (i.id === item.id) {
        i.action?.(i)
        break
      }
    }
  })

  const registerableItems = useMemo(
    () =>
      filteredItems.map((item) => ({
        ...item,
        action: performAction,
      })),
    [filteredItems, performAction],
  )

  useDeepCompareEffect(() => {
    register(key, {
      key,
      groupName,
      items: registerableItems,
      weight,
      serverFiltered,
      page,
    })
  }, [registerableItems, groupName, weight, serverFiltered, page])

  useUnmount(() => {
    unregister(key)
  })
}
